import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, API_ORIGIN } from "../lib/api";
import { Button, EmptyState, ErrorNote, Field, Input } from "./ui";

export interface MediaAsset {
  id: string;
  filename: string;
  mimeType: string;
  bytes: number;
  width: number | null;
  height: number | null;
  alt: string | null;
  createdAt: string;
}

/** Where an uploaded image is served from, for both the picker and the post. */
export function mediaUrl(id: string): string {
  return `${API_ORIGIN}/api/v1/media/${id}`;
}

/**
 * Upload and choose an image.
 *
 * The alt field sits on the upload rather than on each use, because the
 * description belongs to the picture and repeating it per post is how it ends
 * up written once and empty everywhere else. It is optional and says why:
 * a genuinely decorative image has no description, and inventing one is worse
 * than leaving it out.
 */
export function MediaPicker({
  onPick,
  onClose,
}: {
  onPick: (asset: MediaAsset) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [alt, setAlt] = useState("");

  const media = useQuery({
    queryKey: ["media"],
    queryFn: () => api<MediaAsset[]>("/api/v1/admin/media"),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      // FormData rather than JSON: sending an image as base64 inflates it by a
      // third and holds the whole thing in memory as a string on both ends.
      const form = new FormData();
      form.append("file", file);
      if (alt.trim() !== "") form.append("alt", alt.trim());
      return api<MediaAsset>("/api/v1/admin/media", { method: "POST", body: form });
    },
    onSuccess: (asset) => {
      setAlt("");
      if (fileRef.current !== null) fileRef.current.value = "";
      void queryClient.invalidateQueries({ queryKey: ["media"] });
      onPick(asset);
    },
  });

  return (
    <div className="space-y-4 rounded border border-hairline bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm text-ink">Images</h3>
          <p className="text-xs text-dim">
            Re-encoded on upload, so nothing rides along in the metadata. 12MB max.
          </p>
        </div>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Describe it" hint="For anyone who cannot see it. Leave empty if it is purely decorative.">
          <Input
            value={alt}
            placeholder="e.g. a van outside a customer's house"
            onChange={(event) => setAlt(event.target.value)}
          />
        </Field>

        <Field label="Upload" hint="JPEG, PNG, WebP, GIF or AVIF.">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="w-full text-sm text-muted file:mr-3 file:rounded file:border-0 file:bg-accent file:px-3 file:py-2 file:text-sm file:text-base"
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file !== undefined) upload.mutate(file);
            }}
          />
        </Field>
      </div>

      {upload.isPending && <p className="text-sm text-muted">Uploading…</p>}
      {upload.isError && <ErrorNote>{messageFor(upload.error)}</ErrorNote>}

      {media.isPending ? (
        <EmptyState>Loading…</EmptyState>
      ) : (media.data?.length ?? 0) === 0 ? (
        <EmptyState>No images yet.</EmptyState>
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(media.data ?? []).map((asset) => (
            <li key={asset.id}>
              <button
                type="button"
                className="block w-full overflow-hidden rounded border border-hairline hover:border-accent"
                onClick={() => onPick(asset)}
              >
                <img
                  src={mediaUrl(asset.id)}
                  alt=""
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
                <span className="block truncate px-2 py-1 text-left text-xs text-muted">
                  {asset.filename}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "Could not upload that file.";
}
